import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { subscriptionService } from '../../../services/subscriptionService';

interface SubscriptionPlanProps {
  subscription: {
    id: number;
    status: string;
    currentPeriodStart?: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    plan: {
      id: string;
      name: string;
      amount: number;
      currency: string;
      interval: string;
      intervalCount?: number;
    };
    stripeDetails?: {
      status: string;
      currentPeriodEnd: string;
      cancelAtPeriodEnd: boolean;
    };
  } | null;
  onRefresh: () => void;
  onShowPlans?: () => void; // New prop to show pricing plans
}

const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({
  subscription,
  onRefresh,
  onShowPlans
}) => {
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleUpgrade = () => {
    // Use the onShowPlans callback if provided, otherwise fallback to navigate
    if (onShowPlans) {
      onShowPlans();
    } else {
      // Fallback to navigate to billing page which should handle showing plans
      navigate('/settings/billing');
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    try {
      setProcessingAction(true);
      setError(null);

      console.log(`Attempting to cancel subscription with ID: ${subscription.id}`);

      // Use the subscriptionService to cancel the subscription
      await subscriptionService.cancelSubscription(subscription.id);

      console.log('Subscription cancelled successfully');
      alert(
        'Your subscription has been canceled. You will still have access until the end of your current billing period.'
      );

      onRefresh();
      setCancelConfirm(false);
      setProcessingAction(false);
    } catch (err: any) {
      setError(err.message);
      setProcessingAction(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!subscription) return;

    try {
      setProcessingAction(true);

      // Use the subscriptionService to reactivate the subscription
      await subscriptionService.reactivateSubscription(subscription.id);

      onRefresh();
      setProcessingAction(false);
    } catch (err: any) {
      setError(err.message);
      setProcessingAction(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatCurrency = (amount: number, currency: string) => {
    // Stripe returns amounts in cents, but it appears our system is already storing the proper dollar amount
    // Remove the division by 100 to display the correct amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  // We don't need to handle the no subscription case here anymore
  // The parent component (BillingManagement) will handle it
  if (!subscription) {
    return null;
  }

  // Use the most current status from stripe details if available
  const status = subscription.stripeDetails?.status || subscription.status;
  const currentPeriodEnd =
    subscription.stripeDetails?.currentPeriodEnd || subscription.currentPeriodEnd;
  const cancelAtPeriodEnd =
    subscription.stripeDetails?.cancelAtPeriodEnd !== undefined
      ? subscription.stripeDetails.cancelAtPeriodEnd
      : subscription.cancelAtPeriodEnd;

  // Format the plan interval for display
  const formatInterval = () => {
    const interval = subscription.plan.interval;
    const count = subscription.plan.intervalCount || 1;

    if (count === 1) {
      return interval === 'month'
        ? 'monthly'
        : interval === 'year'
          ? 'yearly'
          : interval === 'week'
            ? 'weekly'
            : interval === 'day'
              ? 'daily'
              : interval;
    }

    return `${count} ${interval}${count > 1 ? 's' : ''}`;
  };

  // Get status display style based on subscription status
  const getStatusStyle = () => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-orange-100 text-orange-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      case 'incomplete':
        return 'bg-yellow-100 text-yellow-800';
      case 'incomplete_expired':
        return 'bg-gray-100 text-gray-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status for display
  const formatStatus = (status: string) => {
    return status
      .replace('_', ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium">{subscription.plan.name}</h3>
          {subscription.plan.amount > 0 && (
            <p className="mt-1 text-sm text-gray-400">
              {formatCurrency(subscription.plan.amount, subscription.plan.currency)} /{' '}
              {formatInterval()}
            </p>
          )}
          <div className="mt-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle()}`}
            >
              {formatStatus(status)}
            </span>
          </div>
        </div>
        <button
          onClick={handleUpgrade}
          className={`px-3 py-1 rounded-md text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
        >
          Change Plan
        </button>
      </div>

      <div className="mt-6 space-y-2">
        {subscription.currentPeriodStart && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Current period started</span>
            <span className="text-sm font-medium">
              {formatDate(subscription.currentPeriodStart)}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Current period ends</span>
          <span className="text-sm font-medium">{formatDate(currentPeriodEnd)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Auto-renewal</span>
          <span
            className={`text-sm font-medium ${cancelAtPeriodEnd ? 'text-red-500' : 'text-green-500'}`}
          >
            {cancelAtPeriodEnd ? 'Off' : 'On'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Billing cycle</span>
          <span className="text-sm font-medium">{formatInterval()}</span>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        {cancelAtPeriodEnd ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your subscription will end on {formatDate(currentPeriodEnd)}. You can reactivate your
              subscription to continue service after this date.
            </p>
            <button
              onClick={handleReactivateSubscription}
              disabled={
                processingAction || status === 'incomplete' || status === 'incomplete_expired'
              }
              className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} ${status === 'incomplete' || status === 'incomplete_expired' ? 'opacity-50 cursor-not-allowed' : ''} text-white transition-colors`}
            >
              {processingAction ? 'Processing...' : 'Reactivate Subscription'}
            </button>
          </div>
        ) : (
          <div className="text-right">
            {!cancelConfirm ? (
              <button
                onClick={() => setCancelConfirm(true)}
                disabled={
                  status === 'incomplete' ||
                  status === 'incomplete_expired' ||
                  status === 'canceled'
                }
                className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'} ${status === 'incomplete' || status === 'incomplete_expired' || status === 'canceled' ? 'opacity-50 cursor-not-allowed' : ''} text-white transition-colors`}
              >
                Cancel Subscription
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to cancel? Your subscription will remain active until the
                  end of the current billing period.
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setCancelConfirm(false)}
                    className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                    disabled={processingAction}
                  >
                    No, Keep It
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'} text-white transition-colors`}
                    disabled={processingAction}
                  >
                    {processingAction ? 'Processing...' : 'Yes, Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlan;
